import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { useLocation, useRouter } from '@tanstack/react-router';
import type { Permission } from '@uturn/portal/abac';
import {
	type NavItem,
	Navigation,
} from '@uturn/portal/components/header/navigation';
import { useAuth } from '@uturn/portal/modules/auth/hooks';
import type { FileRoutesByTo } from '@uturn/portal/routeTree.gen';
import { useAbac } from 'react-abac';
import { HeaderLogo } from './header-logo';
import { HeaderUtilities } from './header-utilities';
import { Search } from './search';

export type NavigationItemProps = {
	name: string;
	href: keyof FileRoutesByTo;
	params?: Record<string, unknown>;
	icon: IconDefinition;
	active?: boolean;
	preFetch?: () => void;
	permission?: Permission;
};

export const Header = ({
	userNavigation,
	dropdownItems,
	user,
}: {
	userNavigation: NavigationItemProps[];
	dropdownItems: { name: string; href: string }[];
	user: { name: string; role: string };
}) => {
	const { userHasPermissions } = useAbac();
	const { logout } = useAuth();
	const router = useRouter();
	const pathname = useLocation({
		select: (location) => location.pathname,
	});

	// TODO: Improve this
	const activeNavigation: NavItem[] = userNavigation
		.filter((item) => !item.permission || userHasPermissions(item.permission))
		.map((item) => {
			if (item.href === '/') {
				return {
					...item,
					active: pathname === '/',
				};
			}

			return {
				...item,
				active: pathname.includes(item.href.split('/')[1]),
			};
		});

	const handleLogout = async () => {
		await logout();
		router.navigate({ to: '/sign-in' });
	};

	return (
		<div className="bg-card/95 supports-[backdrop-filter]:bg-card/60 sticky top-0 z-20 border-b backdrop-blur">
			<div className="3xl:w-2/3 3xl:px-0 m-auto flex h-16 items-center px-4 lg:px-8">
				<HeaderLogo linkTo="/shipments" />
				<div className="ml-6 flex w-full flex-row-reverse gap-4 md:flex-row md:gap-0">
					<Navigation navItems={activeNavigation} />
					<div className="ml-auto flex items-center gap-4 md:gap-1 lg:gap-4">
						<Search />
						<HeaderUtilities
							dropdownItems={dropdownItems}
							user={user}
							onLogout={handleLogout}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
